import { useQuery } from "@tanstack/react-query";
import { userInfoQueryOptions } from "./queries/auth";
import type { UserInfo } from "./http/auth";

export function isOrgAdmin(info: UserInfo): boolean {
  return info.org_admin;
}

/** Returns true if the user is a guest — only "Shared with Me" access. */
export function isGuest(info: UserInfo): boolean {
  return info.user_type === "GUEST";
}

/** Non-guest users have full access; guests may only use shared meetings, meeting detail, and profile. */
export function isMemberAllowedPath(pathname: string): boolean {
  if (pathname === "/my-profile" || pathname.startsWith("/my-profile/")) {
    return true;
  }
  if (pathname === "/meetings/shared" || pathname.startsWith("/meetings/shared/")) {
    return true;
  }
  if (/^\/meetings\/[^/]+$/.test(pathname)) {
    const segment = pathname.slice("/meetings/".length);
    return segment !== "shared" && segment !== "scheduled";
  }
  if (/^\/participant-details\/[^/]+\/[^/]+$/.test(pathname)) {
    return true;
  }
  return false;
}

export function canAccess(
  info: UserInfo,
  resource: string,
  actions?: Array<string>,
): boolean {
  if ("all" in info.permissions) return true;

  if (!(resource in info.permissions)) return false;

  if (!actions || actions.length === 0) return true;

  const resourceActions = new Set(info.permissions[resource]);

  return (
    resourceActions.has("all") || resourceActions.isSupersetOf(new Set(actions))
  );
}

export function useResourceAccessPermission(
  resource: string,
  actions?: Array<string>,
) {
  return useQuery({
    ...userInfoQueryOptions,
    select(data) {
      return canAccess(data, resource, actions);
    },
  });
}
